import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import {
  LayoutDashboard, Users, CalendarCog, Gift, Medal, CreditCard,
  ScrollText, Settings as SettingsIcon, Megaphone, Plus, Save, Trash2,
  RefreshCw, Satellite, AlertTriangle, CheckCircle2, Wallet, Tag, MessageCircle,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDateTime, fmtDay, euro } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const TABS = [
  { k: "overview", label: "Dashboard", icon: LayoutDashboard },
  { k: "matches", label: "Partite", icon: CalendarCog },
  { k: "users", label: "Utenti", icon: Users },
  { k: "periods", label: "Periodi & Premi", icon: Gift },
  { k: "winners", label: "Vincitori", icon: Medal },
  { k: "discounts", label: "Codici Sconto", icon: Tag },
  { k: "payments", label: "Pagamenti", icon: CreditCard },
  { k: "broadcast", label: "Comunicazioni", icon: Megaphone },
  { k: "settings", label: "Impostazioni", icon: SettingsIcon },
  { k: "audit", label: "Audit Log", icon: ScrollText },
];

function Field({ label, children }) {
  return <label className="block"><span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span><div className="mt-1">{children}</div></label>;
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <div>
      <PageHeader overline="Area riservata" title="Pannello SuperAdmin" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 border-b border-gold-300/15">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${tab === t.k ? "bg-gold-300 text-ink-900" : "text-slate-300 hover:bg-ink-700"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview />}
        {tab === "matches" && <Matches />}
        {tab === "users" && <UsersTab />}
        {tab === "periods" && <Periods />}
        {tab === "winners" && <Winners />}
        {tab === "discounts" && <Discounts />}
        {tab === "payments" && <Payments />}
        {tab === "broadcast" && <Broadcast />}
        {tab === "settings" && <SettingsTab />}
        {tab === "audit" && <Audit />}
      </div>
    </div>
  );
}

function RevenueBox() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () => api.get("/admin/revenue", { params: { start: start || undefined, end: end || undefined } })
      .then((x) => { if (active) setData(x.data); }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { active = false; clearInterval(t); };
  }, [start, end]);
  return (
    <div className="surface-elevated p-5 mb-6 border-gold-300/30" data-testid="revenue-box">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Wallet className="text-gold-300" size={20} />
        <h3 className="font-heading text-lg font-bold uppercase text-white">Ricavi Totali</h3>
        <span className="ml-auto text-xs text-muted-foreground inline-flex items-center gap-1"><RefreshCw size={12} /> aggiornamento automatico</span>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-muted-foreground block">Data inizio
          <input type="date" className="input-dark !py-2 mt-1 block" value={start} onChange={(e) => setStart(e.target.value)} data-testid="revenue-start" />
        </label>
        <label className="text-xs text-muted-foreground block">Data fine
          <input type="date" className="input-dark !py-2 mt-1 block" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="revenue-end" />
        </label>
        {(start || end) && <button onClick={() => { setStart(""); setEnd(""); }} className="btn-outline-gold !py-2 !px-3 text-xs">Azzera periodo</button>}
      </div>
      <div className="bg-ink-900 rounded-lg p-5">
        <div className="font-heading text-5xl font-black gold-gradient-text tabular-nums" data-testid="revenue-total">{data ? euro(data.total) : "…"}</div>
        <div className="text-sm text-muted-foreground mt-1">
          {data?.count ?? 0} transazioni {(start || end) ? "nel periodo selezionato" : "totali"} · {data?.active_subscriptions ?? 0} abbonamenti attivi
        </div>
      </div>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((x) => setD(x.data)); }, []);
  const cards = d ? [
    ["Utenti", d.users], ["Abb. attivi", d.active_subscriptions], ["Annullati", d.cancelled_subscriptions],
    ["Partite", d.matches], ["Pronostici", d.predictions], ["Pagamenti", d.payments_count],
  ] : [];
  return (
    <div>
      <RevenueBox />
      {d && (<>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map(([l, v]) => (
          <div key={l} className="surface p-5"><div className="text-xs text-muted-foreground uppercase tracking-wide">{l}</div><div className="font-heading text-4xl font-black text-gold-300 mt-1">{v}</div></div>
        ))}
      </div>
      <div className="surface p-5 mt-6">
        <h3 className="font-heading text-lg font-bold uppercase text-white mb-3">Stato Integrazioni</h3>
        <div className="space-y-2 text-sm">
          {[["PayPal (pagamenti)", d.integrations.paypal], ["Email (Resend)", d.integrations.email], ["API Calcistica", d.integrations.football_api]].map(([l, ok]) => (
            <div key={l} className="flex justify-between items-center">
              <span className="text-slate-300">{l}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${ok ? "bg-status-correct/15 text-status-correct" : "bg-status-locked/15 text-status-locked"}`}>{ok ? "Configurato" : "Non configurato"}</span>
            </div>
          ))}
        </div>
      </div>
      </>)}
    </div>
  );
}

function FootballSync({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [season, setSeason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/football-status").then((x) => { setStatus(x.data); setSeason(x.data.season || ""); });
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sync-football${season ? `?season=${season}` : ""}`);
      if (data.errors?.length) toast.warning(`Sync completata con avvisi: ${JSON.stringify(data.errors[0])}`);
      else toast.success(`Sync OK — create ${data.created}, aggiornate ${data.updated}, punti ${data.scored}`);
      await load(); onSynced && onSynced();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!status) return null;
  const sub = status.api?.subscription;
  const reqs = status.api?.requests;
  const last = status.last_summary;
  return (
    <div className="surface p-5 mb-4 lg:col-span-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Satellite size={20} className="text-gold-300" />
          <div>
            <h3 className="font-heading text-lg font-bold uppercase text-white">Sincronizzazione API Calcistica</h3>
            <p className="text-xs text-muted-foreground">
              {status.configured ? <span className="text-status-correct">Configurata</span> : <span className="text-status-locked">Non configurata</span>}
              {sub && <> · Piano {sub.plan}</>}
              {reqs && <> · {reqs.current}/{reqs.limit_day} richieste oggi</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input className="input-dark !py-2 !w-28" value={season} onChange={(e) => setSeason(e.target.value)} placeholder="Stagione" data-testid="sync-season" />
          <button onClick={sync} disabled={busy} className="btn-gold !py-2 !px-4 text-sm" data-testid="sync-football-btn">
            <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> {busy ? "Sincronizzo…" : "Sincronizza ora"}
          </button>
        </div>
      </div>
      {last && (
        <div className="mt-3 text-sm flex flex-wrap gap-4 text-muted-foreground">
          <span>Ultima sync: {status.last_sync ? fmtDateTime(status.last_sync) : "—"}</span>
          <span className="text-status-correct">Create: {last.created}</span>
          <span className="text-gold-300">Aggiornate: {last.updated}</span>
          <span>Punti assegnati: {last.scored}</span>
        </div>
      )}
      {last?.errors?.length > 0 && (
        <div className="mt-3 flex items-start gap-2 bg-status-locked/10 border border-status-locked/30 rounded-lg p-3 text-sm text-status-locked">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>{typeof last.errors[0] === "string" ? last.errors[0] : JSON.stringify(last.errors[0])}</span>
        </div>
      )}
    </div>
  );
}

function Matches() {
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState({ competition_code: "SERIE_A", home_team: "", away_team: "", kickoff: "", matchday: 1 });
  const load = () => api.get("/matches").then((x) => setMatches(x.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/matches", { ...form, matchday: Number(form.matchday), kickoff: new Date(form.kickoff).toISOString() });
      toast.success("Partita creata"); setForm({ ...form, home_team: "", away_team: "", kickoff: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setResult = async (m) => {
    const hs = prompt(`Gol ${m.home_team}?`); if (hs === null) return;
    const as = prompt(`Gol ${m.away_team}?`); if (as === null) return;
    const reason = prompt("Motivazione (facoltativa):") || "";
    try { await api.put(`/admin/matches/${m.id}`, { home_score: Number(hs), away_score: Number(as), status: "finished", reason }); toast.success("Risultato salvato, punti assegnati"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const changeStatus = async (m, status) => {
    try { await api.put(`/admin/matches/${m.id}`, { status }); toast.success("Stato aggiornato"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (m) => { if (!window.confirm("Eliminare la partita?")) return; await api.delete(`/admin/matches/${m.id}`); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <FootballSync onSynced={load} />
      <form onSubmit={create} className="surface p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg font-bold uppercase text-white flex items-center gap-2"><Plus size={18} /> Nuova partita</h3>
        <Field label="Competizione">
          <select className="input-dark" value={form.competition_code} onChange={(e) => setForm({ ...form, competition_code: e.target.value })}>
            <option value="SERIE_A">Serie A</option><option value="UCL">Champions League</option>
          </select>
        </Field>
        <Field label="Squadra casa"><input className="input-dark" required value={form.home_team} onChange={(e) => setForm({ ...form, home_team: e.target.value })} /></Field>
        <Field label="Squadra ospite"><input className="input-dark" required value={form.away_team} onChange={(e) => setForm({ ...form, away_team: e.target.value })} /></Field>
        <Field label="Giornata"><input type="number" className="input-dark" value={form.matchday} onChange={(e) => setForm({ ...form, matchday: e.target.value })} /></Field>
        <Field label="Data e ora (calcio d'inizio)"><input type="datetime-local" className="input-dark" required value={form.kickoff} onChange={(e) => setForm({ ...form, kickoff: e.target.value })} /></Field>
        <button className="btn-gold w-full" data-testid="admin-create-match">Crea partita</button>
      </form>

      <div className="lg:col-span-2 space-y-2">
        {matches.map((m) => (
          <div key={m.id} className="surface p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-white font-semibold">{m.home_team} {m.result ? `${m.home_score}-${m.away_score}` : "vs"} {m.away_team}</div>
              <div className="text-xs text-muted-foreground">{fmtDateTime(m.kickoff)} · {m.competition_code === "UCL" ? "UCL" : "Serie A"} · {m.status}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setResult(m)} className="btn-outline-gold !py-1.5 !px-3 text-xs" data-testid={`admin-set-result-${m.id}`}>Risultato</button>
              <select className="input-dark !py-1.5 !px-2 text-xs !w-auto" value={m.status} onChange={(e) => changeStatus(m, e.target.value)}>
                {["scheduled", "live", "finished", "postponed", "suspended", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => del(m)} className="text-destructive p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((x) => setUsers(x.data));
  useEffect(() => { load(); }, []);
  const grant = async (u) => { try { await api.post(`/admin/grant-subscription/${u.id}`); toast.success("Abbonamento attivato"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (u) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente? L'azione è irreversibile.")) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utente eliminato"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-muted-foreground border-b border-gold-300/15">
          <th className="p-3">Nickname</th><th className="p-3">Email</th><th className="p-3">Ruolo</th><th className="p-3">Abbonamento</th><th className="p-3 text-right">Azioni</th>
        </tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-gold-300/5">
              <td className="p-3 text-white font-medium">{u.nickname}</td>
              <td className="p-3 text-muted-foreground">{u.email}</td>
              <td className="p-3">{u.role === "superadmin" ? <span className="text-gold-300">Admin</span> : "Utente"}</td>
              <td className="p-3">{u.subscription?.status === "active" ? <span className="text-status-correct">Attivo</span> : <span className="text-muted-foreground">—</span>}</td>
              <td className="p-3">
                <div className="flex items-center gap-2 justify-end flex-wrap">
                  {u.whatsapp_number && (
                    <a href={`https://wa.me/${u.whatsapp_dial_code || ""}${u.whatsapp_number}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-status-correct border border-status-correct/40 rounded-full px-3 py-1 text-xs hover:bg-status-correct/10 transition" data-testid={`whatsapp-${u.id}`}>
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                  )}
                  {u.role !== "superadmin" && (
                    <>
                      {u.subscription?.status !== "active" && <button onClick={() => grant(u)} className="btn-outline-gold !py-1 !px-3 text-xs" data-testid={`grant-sub-${u.id}`}>Attiva</button>}
                      <button onClick={() => del(u)} className="inline-flex items-center gap-1 text-destructive border border-destructive/40 rounded-full px-3 py-1 text-xs hover:bg-destructive/10 transition" data-testid={`delete-user-${u.id}`}><Trash2 size={13} /> Elimina</button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Periods() {
  const [periods, setPeriods] = useState([]);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [prize, setPrize] = useState({ period_id: "", position: 1, title: "", description: "", image_url: "" });
  const load = () => api.get("/periods").then((x) => setPeriods(x.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => { e.preventDefault(); try { await api.post("/admin/periods", { ...form, start_date: new Date(form.start_date).toISOString(), end_date: new Date(form.end_date).toISOString() }); toast.success("Periodo creato"); setForm({ name: "", start_date: "", end_date: "" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const activate = async (p) => { await api.post(`/admin/periods/${p.id}/activate`); toast.success("Periodo attivato"); load(); };
  const close = async (p) => { if (!window.confirm("Chiudere il periodo? Verrà salvato uno snapshot della classifica.")) return; const { data } = await api.post(`/admin/periods/${p.id}/close`); toast.success(`Periodo chiuso. Podio: ${data.podium.map((r) => r.nickname).join(", ") || "n/d"}`); load(); };
  const addPrize = async (e) => { e.preventDefault(); try { await api.post("/admin/prizes", { ...prize, position: Number(prize.position) }); toast.success("Premio salvato"); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={create} className="surface p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg font-bold uppercase text-white">Nuovo periodo (30 giorni)</h3>
        <Field label="Nome"><input className="input-dark" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Inizio"><input type="datetime-local" className="input-dark" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
        <Field label="Fine"><input type="datetime-local" className="input-dark" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
        <button className="btn-gold w-full">Crea periodo</button>
      </form>

      <form onSubmit={addPrize} className="surface p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg font-bold uppercase text-white">Configura premio</h3>
        <Field label="Periodo"><select className="input-dark" required value={prize.period_id} onChange={(e) => setPrize({ ...prize, period_id: e.target.value })}><option value="">Seleziona…</option>{periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label="Posizione"><select className="input-dark" value={prize.position} onChange={(e) => setPrize({ ...prize, position: e.target.value })}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}°</option>)}</select></Field>
        <Field label="Titolo"><input className="input-dark" required value={prize.title} onChange={(e) => setPrize({ ...prize, title: e.target.value })} /></Field>
        <Field label="Descrizione"><input className="input-dark" value={prize.description} onChange={(e) => setPrize({ ...prize, description: e.target.value })} /></Field>
        <Field label="URL immagine"><input className="input-dark" value={prize.image_url} onChange={(e) => setPrize({ ...prize, image_url: e.target.value })} /></Field>
        <button className="btn-gold w-full">Salva premio</button>
      </form>

      <div className="lg:col-span-2 space-y-2">
        {periods.map((p) => (
          <div key={p.id} className="surface p-4 flex items-center justify-between flex-wrap gap-2">
            <div><div className="text-white font-semibold">{p.name}</div><div className="text-xs text-muted-foreground">{fmtDay(p.start_date)} → {fmtDay(p.end_date)} · <span className={p.status === "active" ? "text-status-correct" : ""}>{p.status}</span></div></div>
            <div className="flex gap-2">
              {p.status !== "active" && <button onClick={() => activate(p)} className="btn-outline-gold !py-1.5 !px-3 text-xs">Attiva</button>}
              {p.status === "active" && <button onClick={() => close(p)} className="btn-outline-gold !py-1.5 !px-3 text-xs">Chiudi & snapshot</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Winners() {
  const [periods, setPeriods] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ period_id: "", position: 1, user_id: "", status: "assegnato", notes: "" });
  useEffect(() => { api.get("/periods").then((x) => setPeriods(x.data)); api.get("/admin/users").then((x) => setUsers(x.data)); }, []);
  const save = async (e) => { e.preventDefault(); try { await api.post("/admin/winners", { ...form, position: Number(form.position) }); toast.success("Vincitore registrato"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <form onSubmit={save} className="surface p-5 space-y-3 max-w-lg">
      <h3 className="font-heading text-lg font-bold uppercase text-white">Conferma vincitore</h3>
      <p className="text-xs text-muted-foreground">Il vincitore va confermato manualmente in base alla classifica finale del periodo.</p>
      <Field label="Periodo"><select className="input-dark" required value={form.period_id} onChange={(e) => setForm({ ...form, period_id: e.target.value })}><option value="">Seleziona…</option>{periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
      <Field label="Posizione"><select className="input-dark" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}°</option>)}</select></Field>
      <Field label="Utente"><select className="input-dark" required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}><option value="">Seleziona…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.nickname}</option>)}</select></Field>
      <Field label="Stato premio"><select className="input-dark" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="da_assegnare">Da assegnare</option><option value="assegnato">Assegnato</option><option value="consegnato">Consegnato</option></select></Field>
      <Field label="Note / motivazione"><input className="input-dark" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <button className="btn-gold w-full">Registra</button>
    </form>
  );
}

function Discounts() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percentage", value: 50, max_uses: 50 });
  const load = () => api.get("/admin/discounts").then((x) => setCodes(x.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/discounts", { ...form, value: Number(form.value), max_uses: Number(form.max_uses), active: true }); toast.success("Codice creato"); setForm({ code: "", type: "percentage", value: 50, max_uses: 50 }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (c) => { await api.put(`/admin/discounts/${c.id}`, { active: !c.active }); load(); };
  const editField = async (c, patch) => { await api.put(`/admin/discounts/${c.id}`, patch); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="surface p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg font-bold uppercase text-white flex items-center gap-2"><Tag size={18} /> Nuovo codice</h3>
        <Field label="Nome codice (solo lettere/numeri)"><input className="input-dark uppercase" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.replace(/[^a-zA-Z0-9]/g, "") })} data-testid="discount-code-input" /></Field>
        <Field label="Tipo sconto"><select className="input-dark" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="percentage">Percentuale (%)</option><option value="fixed">Importo fisso (€)</option></select></Field>
        <Field label={form.type === "percentage" ? "Valore (%)" : "Valore (€)"}><input type="number" step="0.01" className="input-dark" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Field>
        <Field label="Utilizzi massimi (0 = illimitati)"><input type="number" className="input-dark" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} /></Field>
        <button className="btn-gold w-full" data-testid="create-discount-btn">Crea codice</button>
      </form>

      <div className="lg:col-span-2 surface overflow-x-auto h-fit">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-gold-300/15">
            <th className="p-3">Codice</th><th className="p-3">Tipo</th><th className="p-3">Valore</th><th className="p-3">Utilizzi</th><th className="p-3">Stato</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {codes.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-muted-foreground">Nessun codice sconto.</td></tr> :
              codes.map((c) => (
                <tr key={c.id} className="border-b border-gold-300/5" data-testid={`discount-row-${c.code}`}>
                  <td className="p-3 font-mono-ui text-gold-300 font-bold">{c.code}</td>
                  <td className="p-3 text-slate-300">{c.type === "percentage" ? "Percentuale" : "Importo fisso"}</td>
                  <td className="p-3 text-white">{c.type === "percentage" ? `${c.value}%` : euro(c.value)}</td>
                  <td className="p-3 text-slate-300">{c.used_count}/{c.max_uses === 0 ? "∞" : c.max_uses}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${c.active ? "bg-status-correct/15 text-status-correct" : "bg-ink-700 text-muted-foreground"}`}>{c.active ? "Attivo" : "Disattivato"}</span></td>
                  <td className="p-3">
                    <button onClick={() => toggle(c)} className="btn-outline-gold !py-1 !px-3 text-xs" data-testid={`toggle-discount-${c.code}`}>{c.active ? "Disattiva" : "Attiva"}</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Payments() {
  const [pays, setPays] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((x) => setPays(x.data)); }, []);
  return (
    <div className="surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-muted-foreground border-b border-gold-300/15"><th className="p-3">Data</th><th className="p-3">Utente</th><th className="p-3">Importo</th><th className="p-3">Stato</th></tr></thead>
        <tbody>
          {pays.length === 0 ? <tr><td colSpan="4" className="p-6 text-center text-muted-foreground">Nessuna transazione.</td></tr> :
            pays.map((p) => (<tr key={p.id} className="border-b border-gold-300/5"><td className="p-3 text-muted-foreground">{fmtDateTime(p.created_at)}</td><td className="p-3 text-white">{p.user?.nickname || "—"}</td><td className="p-3 text-gold-300">{euro(p.amount, p.currency)}</td><td className="p-3 text-status-correct">{p.status}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Broadcast() {
  const [form, setForm] = useState({ title: "", body: "", only_active: false });
  const send = async (e) => { e.preventDefault(); try { const { data } = await api.post("/admin/broadcast", form); toast.success(`Inviato a ${data.recipients} utenti`); setForm({ title: "", body: "", only_active: false }); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <form onSubmit={send} className="surface p-5 space-y-3 max-w-lg">
      <h3 className="font-heading text-lg font-bold uppercase text-white">Invia comunicazione</h3>
      <Field label="Titolo"><input className="input-dark" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
      <Field label="Messaggio"><textarea className="input-dark" rows="3" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.only_active} onChange={(e) => setForm({ ...form, only_active: e.target.checked })} /> Solo abbonati attivi</label>
      <button className="btn-gold w-full">Invia notifica</button>
    </form>
  );
}

function SettingsTab() {
  const { loadSettings } = useApp();
  const [form, setForm] = useState({ logo_url: "", subscription_price: 4.99, instagram_url: "", contact_email: "", tiebreaker_rule: "" });
  useEffect(() => { api.get("/settings").then((x) => setForm((f) => ({ ...f, ...x.data, logo_url: x.data.logo_url || "" }))); }, []);
  const save = async (e) => { e.preventDefault(); try { await api.put("/admin/settings", { ...form, subscription_price: Number(form.subscription_price) }); toast.success("Impostazioni salvate"); loadSettings(); } catch (e) { toast.error(apiErr(e)); } };
  const reprice = async () => {
    if (!window.confirm("Aggiornare su PayPal il prezzo di tutti gli abbonamenti attivi al prezzo attuale?")) return;
    try { const { data } = await api.post("/admin/subscriptions/reprice"); toast.success(`Aggiornati ${data.updated} abbonamenti (${data.errors} errori)`); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <form onSubmit={save} className="surface p-5 space-y-3 max-w-lg">
      <h3 className="font-heading text-lg font-bold uppercase text-white flex items-center gap-2"><SettingsIcon size={18} /> Configurazione</h3>
      <Field label="URL Logo (lascia vuoto per il logo testuale)"><input className="input-dark" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" /></Field>
      {form.logo_url && <img src={form.logo_url} alt="anteprima" className="h-14 object-contain" />}
      <Field label="Prezzo abbonamento (€)"><input type="number" step="0.01" className="input-dark" value={form.subscription_price} onChange={(e) => setForm({ ...form, subscription_price: e.target.value })} /></Field>
      <Field label="Instagram URL"><input className="input-dark" value={form.instagram_url || ""} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} /></Field>
      <Field label="Email contatto"><input className="input-dark" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
      <Field label="Regola di spareggio"><textarea className="input-dark" rows="2" value={form.tiebreaker_rule || ""} onChange={(e) => setForm({ ...form, tiebreaker_rule: e.target.value })} /></Field>
      <button className="btn-gold w-full"><Save size={16} /> Salva</button>
      <button type="button" data-testid="admin-reprice-btn" onClick={reprice} className="btn-outline-gold w-full text-sm">Applica il prezzo attuale agli abbonati esistenti</button>
    </form>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((x) => setLogs(x.data)); }, []);
  return (
    <div className="surface p-5 space-y-2">
      {logs.length === 0 ? <p className="text-muted-foreground text-sm">Nessuna operazione registrata.</p> :
        logs.map((l) => (
          <div key={l.id} className="text-sm border-b border-gold-300/5 pb-2">
            <span className="text-gold-300 font-mono-ui">{l.action}</span> <span className="text-slate-300">{l.target_type}</span>
            <span className="text-muted-foreground"> · {l.details} {l.reason && `(${l.reason})`}</span>
            <div className="text-xs text-muted-foreground">{l.admin_email} · {fmtDateTime(l.created_at)}</div>
          </div>
        ))}
    </div>
  );
}
